import React, { useState } from 'react'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import {
  CollapsibleText,
  Container,
  Divider,
  Explainer,
  ListNoticesOrdered,
  ReadMoreButton,
  Title,
  TopMessage,
} from './Notices.styles'
import { VenueModeButton } from '@core/Areas/VenueMode/Components/Shared/VenueModeButton'
import { isReactNativeApp as isNative, isReactNativeApp } from '@mobi/web-native-comms/web'
import { DownloadAppStyled as DownloadApp } from '@core/Areas/Home/Components/Footer/Components/Footer.styles'
import { ExternalLink } from '@core/Areas/Home/Components/Footer/Components/ExternalLink'
import {
  AppleAppStore as AppleAppStoreLogo,
  GooglePlayStore as GooglePlayStoreLogo,
} from '@core/Areas/Home/Components/Footer/Components/Logos'
import { trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { ImpreciseNoticeIOS } from '@core/Areas/VenueMode/Components/Notices/ImpreciseNoticeIOS'
import { ImpreciseNoticeAndroid } from '@core/Areas/VenueMode/Components/Notices/ImpreciseNoticeAndroid'
import {
  BENEFITS_EXPLAINER_TEXT,
  BENEFITS_EXPLAINER_TITLE,
  LOCATION_DISABLED_TEXT,
  LOCATION_DISABLED_TITLE,
  VENUE_FEATURE_NAME,
} from '@core/Areas/VenueMode/constants'
import { useDeviceDetection } from '../../Hooks/useDeviceDetection'

interface ImpreciseNoticeProps {
  onEnablePreciseLocation: () => void
}

export const ImpreciseNotice = ({
  onEnablePreciseLocation,
}: ImpreciseNoticeProps): React.JSX.Element => {
  const { isIOS, isAndroid, isSafari, isChrome, browserName } = useDeviceDetection()
  const [isExpanded, setIsExpanded] = useState(false)

  const renderTopMessage = () => (
    <TopMessage>
      <Title>{BENEFITS_EXPLAINER_TITLE}</Title>
      <Explainer>{BENEFITS_EXPLAINER_TEXT}</Explainer>
    </TopMessage>
  )

  const renderDefaultContent = () => (
    <>
      <b>{LOCATION_DISABLED_TITLE}</b>
      <br />
      <i>{LOCATION_DISABLED_TEXT}</i>
      <ListNoticesOrdered>
        <br />
        <li>
          Enable location services for the <b>device.</b>
        </li>
        <li>
          Allow location access for your <b>browser app</b> (eg, Safari / Chrome / Edge).
        </li>
        <li>
          Allow Location Access for <b>TABtouch.mobi in the browser.</b>
        </li>
        <li>Refresh the page</li>
      </ListNoticesOrdered>
    </>
  )

  const getInfoBoxContent = () => {
    if (isReactNativeApp) {
      return `To access ${VENUE_FEATURE_NAME}, please turn on Precise Location in your phone settings.`
    }
    // Custom iOS instructions currently only available on iOS and Chrome
    if (isIOS && (isSafari || isChrome)) {
      return (
        <ImpreciseNoticeIOS isSafari={isSafari} isChrome={isChrome} browserName={browserName} />
      )
    }
    //  Custom Android instructions currently only available on Chrome
    if (isAndroid && isChrome) {
      return <ImpreciseNoticeAndroid />
    }
    return renderDefaultContent()
  }

  return (
    <Container>
      {renderTopMessage()}
      <InfoBox color='orange' size='sm' iconName='DuocolorAlertTriangle'>
        <CollapsibleText isExpanded={isExpanded}>{getInfoBoxContent()}</CollapsibleText>
        <ReadMoreButton onClick={() => setIsExpanded(prev => !prev)}>
          {isExpanded ? 'Read Less' : 'Read More'}
        </ReadMoreButton>
      </InfoBox>
      {isReactNativeApp && (
        <VenueModeButton onClick={onEnablePreciseLocation}>Enable Precise Location</VenueModeButton>
      )}

      {!isNative && (
        <DownloadApp>
          <Divider>OR</Divider>
          <span className='links'>
            {!isAndroid && (
              <ExternalLink
                href='https://itunes.apple.com/au/app/tabtouch/id472906558?mt=8'
                data-tid-home-ios
                onClick={() => trackExitNavigation('apple-app-store')}
              >
                <AppleAppStoreLogo />
              </ExternalLink>
            )}
            {!isIOS && (
              <ExternalLink
                href='https://play.google.com/store/apps/details?id=com.rwwa.android.tabtouch'
                data-tid-home-android
                onClick={() => trackExitNavigation('android-play-store')}
              >
                <GooglePlayStoreLogo />
              </ExternalLink>
            )}
          </span>
        </DownloadApp>
      )}
    </Container>
  )
}

// =============
// Local Helpers
// =============

function trackExitNavigation(destination: string) {
  trackEvent(keys.navExit, { destination })
}
